import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  PlusCircle,
  MessageSquare,
  Eye,
  Heart,
  Share2,
  Trash2,
  Send,
} from 'lucide-react';
import Swal from 'sweetalert2';
import CreatePostModal from '../../components/modals/CreatePostModal';
import { useSelector } from 'react-redux';
import { apiUrl } from '../../config/Config';

const PostsPage = () => {
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [likesList, setLikesList] = useState([]);
  const [commentsList, setCommentsList] = useState([]);
  const [replyText, setReplyText] = useState({});
  const userdata = useSelector((store) => store.userReducer.userdata);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const res = await axios.get(
          `https://graph.facebook.com/v19.0/me/accounts?access_token=${userdata?.metatoken}`
        );
        setPages(res.data.data);
      } catch (err) {
        console.error('Failed to fetch pages', err);
      }
    };
    fetchPages();
  }, []);

  const handlePageChange = async (pageId) => {
    const page = pages.find((p) => p.id === pageId);
    if (!page) return;
    setSelectedPage(page);

    try {
      const res = await axios.get(
        `https://graph.facebook.com/v19.0/${pageId}/posts?fields=message,created_time,full_picture,from,insights.metric(post_impressions),likes.summary(true),comments.summary(true),shares.summary(true)&access_token=${page.access_token}`
      );

      const enrichedPosts = res.data.data.map((post) => {
        const impressionsMetric = post?.insights?.data?.find(
          (d) => d.name === 'post_impressions'
        );
        return {
          ...post,
          insights: {
            reach: impressionsMetric?.values?.[0]?.value || 0,
          },
          like_count: post?.likes?.summary?.total_count || 0,
          comment_count: post?.comments?.summary?.total_count || 0,
          share_count: post?.shares?.count || 0,
        };
      });

      setPosts(enrichedPosts);
    } catch (err) {
      console.error('Failed to fetch posts with insights', err);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!selectedPage) return;

    try {
      await axios.delete(
        `https://graph.facebook.com/v19.0/${postId}?access_token=${selectedPage.access_token}`
      );
      setPosts((prev) => prev.filter((post) => post.id !== postId));
      Swal.fire('Deleted!', 'Your post has been deleted.', 'success');
    } catch (err) {
      console.error('Failed to delete post', err.response?.data || err.message);
      Swal.fire('Error', 'Failed to delete post', 'error');
    }
  };

  const confirmDelete = (postId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete the post.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeletePost(postId);
      }
    });
  };

  const fetchLikes = async (postId) => {
    // const headers = {
    //   headers: {
    //     "Content-Type": "application/json",
    //     Authorization: "Bearer " + userdata?.token,
    //   },
    // };
    // var data = {
    //   'postid': postId,
    //   'pageaccesstoken': selectedPage.access_token
    // }
    // let resp = await axios
    // .post(apiUrl + "getlikesdata", data, headers)
    // .then((res) => {
    //   if (res.data.status == "success") {
    //     // navigate('/admin/dashboard');
    //   } else {
    //     Swal.fire({
    //       title: '',
    //       text: res.data.message,
    //       icon: 'error',
    //       customClass: {
    //         icon: 'custom-swal-icon' // Apply the custom class
    //       },
    //       width: '350px',
    //       confirmButtonText: 'Ok',
    //   confirmButtonColor: "rgb(59, 151, 196)",
    //       customClass: {
    //           popup: "small-icon-alert",
    //         },
    //     });
    //   }
    // })
    // .catch((err) => {
    //   alert(err.message);
    // });


    try {
      const res = await axios.get(
        `https://graph.facebook.com/v19.0/${postId}/likes?summary=true&limit=100&access_token=${selectedPage.access_token}`
      );
      setLikesList(res.data.data);
      setShowLikesModal(true);
    } catch (err) {
      console.error('Failed to fetch likes', err);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const res = await axios.get(
        `https://graph.facebook.com/v19.0/${postId}/comments?fields=from,message,comments.limit(100){from,message}&summary=true&limit=100&access_token=${selectedPage.access_token}`
      );
      setCommentsList(res.data.data);
      setShowCommentsModal(true);
    } catch (err) {
      console.error('Failed to fetch comments', err);
    }
  };

  const handleReplyToComment = async (commentId) => {
    const replyMessage = replyText[commentId]?.trim();
    if (!replyMessage) return;
    try {
      await axios.post(`https://graph.facebook.com/v19.0/${commentId}/comments`,
        { message: replyMessage },
        { params: { access_token: selectedPage.access_token } });
      Swal.fire('Success', 'Reply sent successfully!', 'success');
      setReplyText((prev) => ({ ...prev, [commentId]: '' }));
      const postWithComment = posts.find(post => commentsList.some(comment => comment.id === commentId));
      if (postWithComment) {
        fetchComments(postWithComment.id);
      }
    } catch (err) {
      console.error('Failed to send reply', err);
      Swal.fire('Error', 'Failed to send reply.', 'error');
    }
  };

  return (
    <section className="posts-section">
      <div className="posts-header">
        <button className="btn-create-post" onClick={() => setShowCreatePostModal(true)}>
          <PlusCircle className="icon-create-post" />
          Create New Post
        </button>

        <select onChange={(e) => handlePageChange(e.target.value)} defaultValue="">
          <option value="" disabled>Select a Facebook Page</option>
          {pages.map((page) => (
            <option key={page.id} value={page.id}>{page.name}</option>
          ))}
        </select>
      </div>

      <ul className="posts-list">
        {posts.map((post) => (
          <li key={post.id} className="post-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span className="platform-label">Facebook</span>
                <span className="post-date">{new Date(post.created_time).toLocaleDateString()}</span>
              </div>
              {post?.from?.id === selectedPage?.id && (
                <button className="delete-post-btn" onClick={() => confirmDelete(post.id)}>
                  <Trash2 size={16} /> Delete
                </button>
              )}
            </div>

            <p className="post-message">{post.message || 'No content available'}</p>

            {post.full_picture && (
              <div className="image-wrapper">
                <img className="post-image" src={post.full_picture} alt="Post" />
                <div className="image-hover-icon" onClick={() => setPreviewImage(post.full_picture)}>
                  <Eye />
                </div>
              </div>
            )}

            <div className="post-stats-grid">
              <div className="stat-box" onClick={() => fetchLikes(post.id)} style={{ cursor: 'pointer' }}>
                <Heart className="stat-icon" />
                <div>
                  <strong>{post.like_count}</strong> <span className="post-engemrnt-tittles">Likes</span>
                </div>
              </div>
              <div className="stat-box" onClick={() => fetchComments(post.id)} style={{ cursor: 'pointer' }}>
                <MessageSquare className="stat-icon" />
                <div>
                  <strong>{post.comment_count}</strong> <span className="post-engemrnt-tittles">Comments</span>
                </div>
              </div>
              <div className="stat-box">
                <Share2 className="stat-icon" />
                <div>
                  <strong>{post.share_count}</strong> <span className="post-engemrnt-tittles">Shares</span>
                </div>
              </div>
              <div className="stat-box">
                <Eye className="stat-icon" />
                <div>
                  <strong>{post?.insights?.reach || 0}</strong> <span className="post-engemrnt-tittles">Reach</span>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {previewImage && (
        <div className="image-modal" onClick={() => setPreviewImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={previewImage} alt="Preview" />
            <button className="close-btn" onClick={() => setPreviewImage(null)}>Close</button>
          </div>
        </div>
      )}

      {showCommentsModal && (
        <div className="post-engement-modal-overlay" onClick={() => setShowCommentsModal(false)}>
          <div className="post-engement-custom-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Comments</h3>
            <div className="engagement-user-list">
              {commentsList.map((comment) => (
                <div className="engagement-user-row" key={comment.id}>
                  <div className="postcomment-avatar ">{comment?.from?.name?.charAt(0).toUpperCase() || 'U'}</div>
                  <div className="name-message">
                    <div className="name">{comment?.from?.name || 'Unknown'}</div>
                    <div className="message">{comment?.message || '(No message)'}</div>
                    <div className="reply-box-under">
                      <input
                        type="text"
                        placeholder="Write a reply..."
                        className="reply-input-under"
                        value={replyText[comment.id] || ''}
                        onChange={(e) => setReplyText((prev) => ({ ...prev, [comment.id]: e.target.value }))}
                      />
                      <button className="reply-send-btn-under" onClick={() => handleReplyToComment(comment.id)}>
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="modal-close-btn" onClick={() => setShowCommentsModal(false)}>Close</button>
          </div>
        </div>
      )}

      {showLikesModal && (
        <div className="post-engement-modal-overlay" onClick={() => setShowLikesModal(false)}>
          <div className="post-engement-custom-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Likes</h3>
            <div className="engagement-user-list">
              {likesList.map((like, index) => (
                <div className="engagement-user-row" key={index}>
                  <div className="post-avatar">{like?.name?.charAt(0).toUpperCase() || 'U'}</div>
                  <div className="name-message">
                    <div className="name">{like?.name || 'Unknown User'}</div>
                  </div>
                </div>
              ))}
            </div>
            <button className="modal-close-btn" onClick={() => setShowLikesModal(false)}>Close</button>
          </div>
        </div>
      )}

      <CreatePostModal
        isOpen={showCreatePostModal}
        onClose={() => setShowCreatePostModal(false)}
        allpages={pages}
        selectedPage={selectedPage}
      />
    </section>
  );
};

export default PostsPage;

